import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter } from "@angular/router";
import {
  MSAL_INSTANCE,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration,
  MsalInterceptor,
} from "@azure/msal-angular";
import { routes } from "./app.routes";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { ConfigService } from "./shared/services/config/config.service";
import {
  PublicClientApplication,
  BrowserCacheLocation,
  LogLevel,
  InteractionType,
} from "@azure/msal-browser";
import { ToastrModule } from "ngx-toastr";
import { loadingInterceptor } from "./shared/http-interceptors/loader/loading.interceptor";
import { userRolesInterceptor } from "./shared/http-interceptors/user-role/user-role.interceptor";
import { errorInterceptor } from "./shared/http-interceptors/error/error.interceptor";
import { registerLocaleData } from "@angular/common";
import localeEn from "@angular/common/locales/en";
import localeEnExtra from "@angular/common/locales/extra/en";

// Register the locale data
registerLocaleData(localeEn, "en-US", localeEnExtra);

async function initializeApp(): Promise<void> {
  const configService = inject(ConfigService)!;
  return await configService.loadConfigurations();
}

export function MSALInstanceFactory(
  configService: ConfigService
): PublicClientApplication {
  const authConfig = configService.getConfigProperty("auth");

  if (!authConfig) {
    throw new Error("Authentication configuration is missing!");
  }

  return new PublicClientApplication({
    auth: {
      clientId: authConfig?.clientId ?? "",
      authority: authConfig?.authority,
      redirectUri: authConfig?.redirectUri,
      postLogoutRedirectUri: authConfig?.postLogoutUrl,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true,
      secureCookies: true,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(
  configService: ConfigService
): MsalGuardConfiguration {
  const authConfig = configService.getConfigProperty("auth");
  if (!authConfig) {
    throw new Error("Authentication configuration is missing!");
  }
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: authConfig.scopes,
    },
  };
}

// provides authRequest configuration
export function MSALInterceptorConfigFactory(
  configService: ConfigService
): MsalInterceptorConfiguration {
  const apiUrl = configService.getConfigProperty("API_URL");
  const scope = configService.getConfigProperty("auth")?.scopes;

  if (!apiUrl || !scope) {
    throw new Error(
      "API_URL or authentication scopes are missing in the configuration."
    );
  }

  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(apiUrl, scope);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withFetch(),
      withInterceptors([
        loadingInterceptor,
        userRolesInterceptor,
        errorInterceptor,
      ])
    ),
    provideAppInitializer(initializeApp),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ConfigService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [ConfigService],
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [ConfigService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    importProvidersFrom(
      ToastrModule.forRoot({
        timeOut: 3500,
        positionClass: "toast-bottom-center",
        preventDuplicates: true,
      })
    ),
  ],
};
